/**
 * ========================
 * EVENTS DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu for the Events module (the church calendar hub).
 * Sections:
 *  1. Overview: Dashboard
 *  2. Events: Events, Review
 *  3. Planning Center: Sources, Mappings
 *  4. Automation: Rules, Learning, Tags (Stage 1B), Playbooks (Stage 2); each needs its right
 *  5. Settings: Event types
 *
 * Visibility is "role OR right": Admin / EventsAdmin / "Events Access", or the matching Event.*
 * right (rights can be granted per user without a role). createVisibilityCheck would AND roles
 * with rights, so the checks are written out here. Review needs Event.Review, which the
 * read-only "Events Access" role does not carry. The "Events Coordinator" role (Event.View and
 * Event.Link.Manage) opens the Dashboard and Events only.
 */

import { IconBolt, IconBulb, IconCalendarEvent, IconCategory, IconClipboardCheck, IconDashboard, IconListCheck, IconPlugConnected, IconRoute, IconTags } from '@tabler/icons-react';
import { DomainMenuProvider, MenuItemVisibility, MenuSection } from '../../layouts/menu/types';

const ADMIN_ROLES = ['Admin', 'admin', 'EventsAdmin'];
const MODULE_ROLES = [...ADMIN_ROLES, 'Events Access'];
/** Roles that carry Event.View (the coordinator role carries no other read right). */
const VIEW_ROLES = [...MODULE_ROLES, 'Events Coordinator'];

/** Read surfaces: any module role, or the right. */
const roleOrRight =
  (right: string): MenuItemVisibility =>
  (ctx) =>
    MODULE_ROLES.some((r) => ctx.userRoles.includes(r)) || ctx.userPermissions.includes(right);

/** Write-only surfaces: an admin role, or the right. */
const adminOrRight =
  (right: string): MenuItemVisibility =>
  (ctx) =>
    ADMIN_ROLES.some((r) => ctx.userRoles.includes(r)) || ctx.userPermissions.includes(right);

class EventsMenuProvider implements DomainMenuProvider {
  domainId = 'events';

  getMenuConfig() {
    const viewers: MenuItemVisibility = (ctx) => VIEW_ROLES.some((r) => ctx.userRoles.includes(r)) || ctx.userPermissions.includes('Event.View');
    const ruleManagers = adminOrRight('Event.Rule.Manage');
    const learningReviewers = adminOrRight('Event.Learning.Review');
    const tagManagers = adminOrRight('Event.Tag.Manage');
    const playbookManagers = adminOrRight('Event.Playbook.Manage');
    const sourceViewers = roleOrRight('Event.Source.View');
    const typeViewers = roleOrRight('Event.Type.View');

    const sections: MenuSection[] = [
      {
        id: 'events-overview',
        label: 'Overview',
        visible: viewers,
        items: [{ id: 'events-dashboard', label: 'Dashboard', path: '/app/events', icon: <IconDashboard size={16} />, visible: viewers }],
      },
      {
        id: 'events-calendar-section',
        label: 'Events',
        visible: viewers,
        items: [
          { id: 'events-list', label: 'Events', path: '/app/events/events', icon: <IconCalendarEvent size={16} />, visible: viewers },
          { id: 'events-review', label: 'Review', path: '/app/events/review', icon: <IconClipboardCheck size={16} />, visible: adminOrRight('Event.Review') },
        ],
      },
      {
        id: 'events-planning-center',
        label: 'Planning Center',
        visible: sourceViewers,
        items: [
          { id: 'events-sources', label: 'Sources', path: '/app/events/sources', icon: <IconPlugConnected size={16} />, visible: sourceViewers },
          { id: 'events-mappings', label: 'Mappings', path: '/app/events/mappings', icon: <IconRoute size={16} />, visible: sourceViewers },
        ],
      },
      {
        id: 'events-automation',
        label: 'Automation',
        visible: (ctx) => ruleManagers(ctx) || learningReviewers(ctx) || tagManagers(ctx) || playbookManagers(ctx),
        items: [
          { id: 'events-rules', label: 'Rules', path: '/app/events/rules', icon: <IconBolt size={16} />, visible: ruleManagers },
          { id: 'events-learning', label: 'Learning', path: '/app/events/learning', icon: <IconBulb size={16} />, visible: learningReviewers },
          { id: 'events-tags', label: 'Tags', path: '/app/events/tags', icon: <IconTags size={16} />, visible: tagManagers },
          { id: 'events-playbooks', label: 'Playbooks', path: '/app/events/playbooks', icon: <IconListCheck size={16} />, visible: playbookManagers },
        ],
      },
      {
        id: 'events-settings',
        label: 'Settings',
        visible: typeViewers,
        items: [{ id: 'events-types', label: 'Event types', path: '/app/events/types', icon: <IconCategory size={16} />, visible: typeViewers }],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '1.0.0',
        lastUpdated: Date.now(),
        description: 'Events hub menu',
      },
    };
  }
}

export const eventsMenuProvider = new EventsMenuProvider();
